/**
 * NotFound — real 404 page.
 *
 * Replaces the old catch-all <Navigate to="/" replace/>, which silently
 * sent every unknown URL to the home page — a soft-404 that pollutes search
 * indexes and hides broken inbound links. This renders a clear not-found
 * state and carries a noindex meta (useNoindex). Issue SEO-006.
 *
 * A genuine HTTP 404 *status* for unknown paths depends on the public-route
 * prerendering work (SEO-001/002): a pure SPA serves index.html with a 200
 * for every path. This page is the content fix; the status-code fix lands
 * with prerendering.
 */
import { Link } from "react-router-dom";
import { Wordmark } from "../components/index.js";
import { useNoindex } from "../lib/use-noindex.js";

export default function NotFound() {
  useNoindex();
  return (
    <div
      data-component="NotFound"
      style={{ maxWidth: 640, margin: "0 auto", padding: "80px 24px", minHeight: "60vh" }}
    >
      <div style={{ marginBottom: 32 }}>
        <Link to="/" aria-label="Interaction Engineering home" style={{ textDecoration: "none" }}>
          <Wordmark scale={2.2} />
        </Link>
      </div>

      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-coral)",
          margin: "0 0 12px",
        }}
      >
        Error 404
      </p>

      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-4xl)",
          color: "var(--ppr-color-accent-navy)",
          letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
          lineHeight: "var(--ppr-typography-line-height-tight)",
          margin: "0 0 16px",
        }}
      >
        Page not found
      </h1>

      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-md)",
          color: "var(--ppr-color-text-secondary)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          margin: "0 0 24px",
        }}
      >
        The page you're looking for doesn't seem to exist, or it may have
        moved. Check the address, or head back to a known starting point.
      </p>

      <p style={{ marginTop: 8 }}>
        <Link
          to="/"
          style={{
            fontFamily: "var(--ppr-typography-font-family-ui)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            color: "var(--ppr-color-accent-navy)",
          }}
        >
          ← Back to Interaction Engineering
        </Link>
      </p>
    </div>
  );
}
